// TopTokenSlider.jsx

import { Box, Stack } from '@mui/material'
import Select from 'components/Select'
import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'
import Slider from 'react-slick'
import TokenDetailModal from 'components/Modal/TokenDetailModal'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import styles from './TopTokenSlider.module.sass'
import RefreshIcon from '@mui/icons-material/Refresh';
import Spinner from 'components/Spinner'

interface TopTokenSliderProps {
  onRefresh?: () => void;
}

const TopTokenSlider: React.FC<TopTokenSliderProps> = ({ onRefresh }) => {

  const topTokenPriceChange = useSelector(
    (state: RootState) => state.user.topTokenPriceChange
  )
  const [selectTopToken, setSelectTopToken] = useState('gainers')
  const [selectedToken, setSelectedToken] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showSpinner, setShowSpinner] = useState(true)

  // Timeout logic
  useEffect(() => {
    if (topTokenPriceChange?.[selectTopToken] === undefined) {
      setShowSpinner(true)
      const timeout = setTimeout(() => {
        setShowSpinner(false)
      }, 8000)
      
      return () => clearTimeout(timeout)
    } else {
      setShowSpinner(false)
    }
  }, [topTokenPriceChange, selectTopToken])

  const settings = {
    dots: false,
    arrows: false,
    infinite: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    variableWidth: true,
    speed: 5000,
    centerMode: true,
    initialSlide: 1,
    autoplaySpeed: 0,
    cssEase: 'linear',
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        }
      }
    ]
  }

  const handleTokenClick = (token) => {
    setSelectedToken(token)
    setIsModalOpen(true)
  }

  // === LOADING STATE ===
  if (topTokenPriceChange?.[selectTopToken] === undefined && showSpinner) {
    return (
      <div className={styles.emptyStateContainer}>
        <Spinner />
      </div>
    )
  }

  // === EMPTY STATE ===
  if (!topTokenPriceChange?.[selectTopToken]?.length) {
    return (
      <div className={styles.emptyStateContainer}>
        <button onClick={onRefresh} className={styles.refreshButton}>
          <RefreshIcon style={{ fontSize: 16, marginRight: '4px' }} />
          Refresh
        </button>
      </div>
    );
  }

  return (
    <>
      <Box display={{ sm: 'block', xs: 'none' }}>
        <Select
          className={styles.selectToken}
          options={[
            {
              title: 'Gainers',
              value: 'gainers',
            },
            {
              title: 'Losers',
              value: 'losers',
            },
          ]}
          setSelected={(topToken) => setSelectTopToken(topToken)}
          selected={selectTopToken}
        />
      </Box>
      <Box
        sx={{
          maxWidth: '100%',
          overflow: 'hidden',
          px: { sm: 3, xs: 2 },
          marginLeft: 'auto',
          display: { sm: 'block', xs: 'none' },
          maxHeight: 64,
          '& .slick-track': { 
            display: 'flex',
            alignItems: 'center',
          },
          '& .slick-slide': {
            opacity: 0.7,
            transition: 'opacity 0.3s ease',
            '&.slick-active': {
              opacity: 1
            }
          }
        }}
      >
        <Slider {...settings}>
          {topTokenPriceChange?.[selectTopToken] &&
            topTokenPriceChange?.[selectTopToken]?.map(
              (data: object, i: number) => {
                const hasMinus = data?.price_24h_percent_change.includes('-')
                const hasMinusIn7d = data?.price_7d_percent_change.includes('-')
                return (
                  <div key={i}>
                    <Stack
                      className={styles.tokenBox}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      onClick={() => handleTokenClick(data)}
                    >
                      <img src={data?.token_logo} alt="" />
                      <Box>
                        <h5>{data?.token_name}</h5>
                        <Stack direction="row" spacing={1}>
                          <p
                            style={{
                              color: !hasMinus ? '#73F6FF' : '#FF5C5C',
                            }}
                          >
                            <span className="timeLabel">24H</span>{' '}
                            {Number(data?.price_24h_percent_change).toFixed(2)}%
                          </p>
                          <p
                            style={{
                              color: !hasMinusIn7d ? '#73F6FF' : '#FF5C5C',
                            }}
                          >
                            <span className="timeLabel">7D</span>{' '}
                            {Number(data?.price_7d_percent_change).toFixed(2)}%
                          </p>
                        </Stack>
                      </Box>
                    </Stack>
                  </div>
                )
              }
            )}
        </Slider>
      </Box>

      <TokenDetailModal
        token={selectedToken}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  )
}

export default TopTokenSlider